import { useEffect, useMemo, useState } from 'react';
import {
  addSupplier,
  addSupplyItemMappings,
  applySupplierAutoPersistenceSnapshot,
  approveDraft,
  approvalModeLabels,
  buildCoupangMcpScenario,
  connectorStatusLabels,
  createSupplierAutoPersistenceFingerprint,
  createSupplierAutoPersistenceSnapshot,
  createSupplierAutoSosAdapterPayload,
  createInitialSupplierAutoState,
  formatSupplierAutoMoney,
  getSupplierAutoSaveCheck,
  holdDraft,
  orderChannelLabels,
  patchSupplier,
  patchSupplierBusinessProfile,
  patchSupplierExternalMarketProfile,
  patchSupplierPaymentPolicy,
  patchSupplierSettlementPolicy,
  patchSupplyItem,
  paymentModeLabels,
  removeSupplyItemMapping,
  settlementCycleLabels,
  setDraftLineExcluded,
  setDraftLineQuantity,
  setSelectedSupplier,
  type CoupangMcpScenario,
  type SupplierAutoConnectorStatus,
  type SupplierAutoDraft,
  type SupplierAutoMaterial,
  type SupplierAutoOrderChannel,
  type SupplierAutoPersistenceAdapter,
  type SupplierAutoPersistenceSnapshot,
  type SupplierAutoPaymentMode,
  type SupplierAutoApprovalMode,
  type SupplierAutoSosAdapterPayload,
  type SupplierAutoSosSupplierUpsert,
  type SupplierAutoSettlementCycle,
  type SupplierAutoState,
  type SupplierAutoStatus,
  type SupplierAutoValidationIssue,
  validateSupplierSetup,
} from '@dvvb/supplier-auto-core';

export interface SupplierAutoLabProps {
  initialState?: SupplierAutoState;
  persistenceAdapter?: SupplierAutoPersistenceAdapter;
  onStateChange?: (state: SupplierAutoState) => void;
}

const paymentModeOptions = Object.entries(paymentModeLabels) as Array<[SupplierAutoPaymentMode, string]>;
const orderChannelOptions = Object.entries(orderChannelLabels) as Array<[SupplierAutoOrderChannel, string]>;
const connectorStatusOptions = Object.entries(connectorStatusLabels) as Array<[SupplierAutoConnectorStatus, string]>;
const approvalModeOptions = Object.entries(approvalModeLabels) as Array<[SupplierAutoApprovalMode, string]>;
const settlementCycleOptions = Object.entries(settlementCycleLabels) as Array<[SupplierAutoSettlementCycle, string]>;
const coupangScenarioStatusLabels: Record<CoupangMcpScenario['status'], string> = {
  ready: 'ready',
  review_required: 'reviewRequired',
  blocked: 'blocked',
};
const coupangStageStatusLabels: Record<CoupangMcpScenario['stages'][number]['status'], string> = {
  ready: 'ready',
  review_required: 'review',
  blocked: 'blocked',
};

export function SupplierAutoLab({ initialState, persistenceAdapter, onStateChange }: SupplierAutoLabProps) {
  const [state, setState] = useState<SupplierAutoState>(() => initialState ?? createInitialSupplierAutoState());
  const [savedSnapshot, setSavedSnapshot] = useState<SupplierAutoPersistenceSnapshot>(() => (
    createSupplierAutoPersistenceSnapshot(initialState ?? createInitialSupplierAutoState())
  ));
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierStatusFilter, setSupplierStatusFilter] = useState<SupplierAutoStatus | 'all'>('all');
  const [materialSearch, setMaterialSearch] = useState('');
  const [pendingMaterialIds, setPendingMaterialIds] = useState<string[]>([]);

  const commitState = (recipe: (current: SupplierAutoState) => SupplierAutoState) => {
    setState((current) => {
      const next = recipe(current);
      onStateChange?.(next);
      return next;
    });
  };

  const selectedSupplier = state.suppliers.find((supplier) => supplier.id === state.selectedSupplierId)
    ?? state.suppliers[0];
  const activeDraft = state.drafts.find((draft) => draft.id === state.activeDraftId)
    ?? state.drafts.find((draft) => draft.supplierId === selectedSupplier?.id)
    ?? state.drafts[0]
    ?? null;
  const activeDraftSupplier = activeDraft
    ? state.suppliers.find((supplier) => supplier.id === activeDraft.supplierId)
    : null;
  const coupangMcpScenario = useMemo(
    () => buildCoupangMcpScenario(activeDraft, activeDraftSupplier),
    [activeDraft, activeDraftSupplier],
  );

  const activeSupplierCount = state.suppliers.filter((supplier) => supplier.status === 'active').length;
  const autoItemCount = state.suppliers.reduce((sum, supplier) => (
    sum + supplier.supplyItems.filter((item) => item.autoOrderEnabled).length
  ), 0);
  const warningCount = state.drafts.reduce((sum, draft) => sum + draft.warnings.length, 0);
  const setupIssueCount = state.suppliers.reduce((sum, supplier) => sum + validateSupplierSetup(supplier).length, 0);
  const saveCheck = getSupplierAutoSaveCheck(state);
  const adapterPayload = useMemo(() => createSupplierAutoSosAdapterPayload(state, {
    existingSuppliers: savedSnapshot.sosSupplierRecords,
    savedAt: savedSnapshot.savedAt,
  }), [state, savedSnapshot.savedAt, savedSnapshot.sosSupplierRecords]);
  const currentFingerprint = createSupplierAutoPersistenceFingerprint(state);
  const savedFingerprint = createSupplierAutoPersistenceFingerprint(savedSnapshot);
  const hasUnsavedChanges = currentFingerprint !== savedFingerprint;
  const saveStateLabel = isLoadingSnapshot
    ? '불러오는 중'
    : isSaving
    ? '저장 중'
    : saveError
      ? '저장 실패'
      : hasUnsavedChanges
        ? saveCheck.canSave ? '변경됨' : '저장 차단'
        : '저장됨';
  const normalizedSupplierSearch = supplierSearch.trim().toLowerCase();
  const visibleSuppliers = state.suppliers.filter((supplier) => {
    const statusMatched = supplierStatusFilter === 'all' || supplier.status === supplierStatusFilter;
    if (!statusMatched) {
      return false;
    }

    if (!normalizedSupplierSearch) {
      return true;
    }

    return [
      supplier.name,
      supplier.ownerName,
      supplier.smsRecipient,
      supplier.businessProfile.businessName,
      supplier.externalMarketProfile.marketName,
      supplier.externalMarketProfile.accountLabel,
    ].some((value) => value.toLowerCase().includes(normalizedSupplierSearch));
  });
  const mappedMaterialIds = new Set(selectedSupplier?.supplyItems.map((item) => item.materialId) ?? []);
  const pendingMaterialIdSet = new Set(pendingMaterialIds);
  const normalizedMaterialSearch = materialSearch.trim().toLowerCase();
  const materialChoices = state.materials.filter((material) => {
    if (!normalizedMaterialSearch) {
      return true;
    }

    return [
      material.name,
      material.category,
      material.locationName,
      material.orderUnit,
      material.comparisonUnit,
    ].some((value) => value.toLowerCase().includes(normalizedMaterialSearch));
  });
  const pendingMaterials = pendingMaterialIds
    .map((materialId) => state.materials.find((material) => material.id === materialId))
    .filter((material): material is SupplierAutoMaterial => Boolean(material));
  const selectedSupplierIssues = selectedSupplier ? validateSupplierSetup(selectedSupplier) : [];
  const selectedSupplierSosUpsert = selectedSupplier
    ? adapterPayload.upserts.find((upsert) => upsert.supplierAutoId === selectedSupplier.id) ?? null
    : null;

  const selectedSupplierDrafts = useMemo(() => {
    if (!selectedSupplier) {
      return state.drafts;
    }

    const scoped = state.drafts.filter((draft) => draft.supplierId === selectedSupplier.id);
    return scoped.length > 0 ? scoped : state.drafts;
  }, [selectedSupplier, state.drafts]);

  const supplierDraftTotal = selectedSupplierDrafts.reduce((sum, draft) => sum + draft.totalAmount, 0);

  useEffect(() => {
    if (!persistenceAdapter) {
      return;
    }

    let cancelled = false;
    setIsLoadingSnapshot(true);
    setSaveError('');
    Promise.resolve(persistenceAdapter.load())
      .then((snapshot) => {
        if (cancelled) {
          return;
        }

        setSavedSnapshot(snapshot);
        setState((current) => {
          const restored = applySupplierAutoPersistenceSnapshot(current, snapshot);
          onStateChange?.(restored);
          return restored;
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setSaveError(error instanceof Error ? error.message : '불러오기 실패');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSnapshot(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [persistenceAdapter, onStateChange]);

  const handleSave = async () => {
    if (!saveCheck.canSave) {
      setSaveError('차단 오류를 먼저 해결해야 저장할 수 있습니다.');
      return;
    }

    const snapshot = createSupplierAutoPersistenceSnapshot(state);
    setIsSaving(true);
    setSaveError('');
    try {
      const saved = persistenceAdapter
        ? await persistenceAdapter.save(snapshot)
        : snapshot;
      setSavedSnapshot(saved);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : '저장 실패');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    const restored = applySupplierAutoPersistenceSnapshot(state, savedSnapshot);
    setSaveError('');
    setMaterialSearch('');
    setPendingMaterialIds([]);
    setState(restored);
    onStateChange?.(restored);
  };

  if (!selectedSupplier) {
    return <div className="supplier-auto-empty">거래처 데이터 없음</div>;
  }

  return (
    <section className="supplier-auto-lab" aria-label="거래처 자동발주 Lab">
      <header className="supplier-auto-header">
        <div>
          <p className="supplier-auto-kicker">거래처 자동발주 Lab</p>
          <h1>거래처 자동발주</h1>
        </div>
        <div className="supplier-auto-metrics" aria-label="요약">
          <Metric label="활성 거래처" value={activeSupplierCount.toString()} />
          <Metric label="자동발주 품목" value={autoItemCount.toString()} />
          <Metric label="발주 후보" value={state.drafts.length.toString()} />
          <Metric label="설정 확인" value={setupIssueCount.toString()} tone={setupIssueCount > 0 ? 'warning' : 'default'} />
          <Metric label="후보 경고" value={warningCount.toString()} tone={warningCount > 0 ? 'warning' : 'default'} />
        </div>
      </header>

      <div className={`supplier-auto-savebar ${hasUnsavedChanges ? 'is-dirty' : ''} ${!saveCheck.canSave ? 'is-blocked' : ''}`} aria-label="거래처 저장 상태">
        <div>
          <strong>{saveStateLabel}</strong>
          <span>
            {isLoadingSnapshot ? 'API에서 거래처를 불러오는 중' : `마지막 저장 ${formatTime(savedSnapshot.savedAt)}`}
            {saveCheck.blockingIssueCount > 0 && ` · 차단 ${saveCheck.blockingIssueCount}건`}
            {saveCheck.warningIssueCount > 0 && ` · 확인 ${saveCheck.warningIssueCount}건`}
          </span>
          {saveError && <p>{saveError}</p>}
        </div>
        <div className="supplier-auto-save-actions">
          <button
            type="button"
            disabled={!hasUnsavedChanges || isSaving || isLoadingSnapshot}
            onClick={handleDiscard}
          >
            되돌리기
          </button>
          <button
            type="button"
            disabled={!hasUnsavedChanges || isSaving || isLoadingSnapshot || !saveCheck.canSave}
            onClick={() => void handleSave()}
          >
            변경사항 저장
          </button>
        </div>
      </div>

      <div className="supplier-auto-grid">
        <aside className="supplier-auto-panel supplier-auto-suppliers" aria-label="거래처 목록">
          <div className="supplier-auto-panel-title">
            <h2>거래처</h2>
            <button
              className="supplier-auto-title-button"
              type="button"
              onClick={() => {
                setMaterialSearch('');
                setPendingMaterialIds([]);
                commitState((current) => addSupplier(current));
              }}
            >
              새 거래처
            </button>
          </div>
          <div className="supplier-auto-list-tools">
            <label>
              거래처 검색
              <input
                value={supplierSearch}
                onChange={(event) => setSupplierSearch(event.target.value)}
              />
            </label>
            <label>
              상태
              <select
                value={supplierStatusFilter}
                onChange={(event) => setSupplierStatusFilter(event.target.value as SupplierAutoStatus | 'all')}
              >
                <option value="all">전체</option>
                <option value="active">사용</option>
                <option value="paused">중지</option>
              </select>
            </label>
          </div>
          <div className="supplier-auto-list">
            {visibleSuppliers.length === 0 && (
              <div className="supplier-auto-empty is-compact">검색 결과 없음</div>
            )}
            {visibleSuppliers.map((supplier) => {
              const draft = state.drafts.find((candidate) => candidate.supplierId === supplier.id);
              const issues = validateSupplierSetup(supplier);
              return (
                <button
                  key={supplier.id}
                  className={`supplier-auto-list-item ${supplier.id === selectedSupplier.id ? 'is-active' : ''}`}
                  type="button"
                  onClick={() => {
                    setMaterialSearch('');
                    setPendingMaterialIds([]);
                    commitState((current) => setSelectedSupplier(current, supplier.id));
                  }}
                >
                  <span>
                    <strong>{supplier.name}</strong>
                    <small>{orderChannelLabels[supplier.orderChannel]} · {paymentModeLabels[supplier.paymentPolicy.mode]}</small>
                    <small>{supplier.supplyItems.length}개 공급물품 · 확인 {issues.length}건</small>
                  </span>
                  <em>{draft ? formatSupplierAutoMoney(draft.totalAmount) : '후보 없음'}</em>
                </button>
              );
            })}
          </div>
        </aside>

        <main className="supplier-auto-main">
          <section className="supplier-auto-panel supplier-auto-master" aria-label="거래처 등록 정보">
            <div className="supplier-auto-panel-title">
              <h2>거래처 등록 정보</h2>
              <span>{selectedSupplier.status === 'active' ? '사용' : '중지'}</span>
            </div>

            <div className="supplier-auto-form-grid">
              <label>
                거래처명
                <input
                  value={selectedSupplier.name}
                  onChange={(event) => commitState((current) => patchSupplier(current, selectedSupplier.id, { name: event.target.value }))}
                />
              </label>
              <label>
                담당자
                <input
                  value={selectedSupplier.ownerName}
                  onChange={(event) => commitState((current) => patchSupplier(current, selectedSupplier.id, { ownerName: event.target.value }))}
                />
              </label>
              <label>
                주문 수신번호
                <input
                  value={selectedSupplier.smsRecipient}
                  onChange={(event) => commitState((current) => patchSupplier(current, selectedSupplier.id, { smsRecipient: event.target.value }))}
                />
              </label>
              <label>
                상호명
                <input
                  value={selectedSupplier.businessProfile.businessName}
                  onChange={(event) => commitState((current) => patchSupplierBusinessProfile(current, selectedSupplier.id, { businessName: event.target.value }))}
                />
              </label>
              <label>
                사업자번호
                <input
                  value={selectedSupplier.businessProfile.businessNumber}
                  onChange={(event) => commitState((current) => patchSupplierBusinessProfile(current, selectedSupplier.id, { businessNumber: event.target.value }))}
                />
              </label>
              <label>
                대표번호
                <input
                  value={selectedSupplier.businessProfile.mainPhone}
                  onChange={(event) => commitState((current) => patchSupplierBusinessProfile(current, selectedSupplier.id, { mainPhone: event.target.value }))}
                />
              </label>
              <label>
                발주 마감
                <input
                  type="time"
                  value={selectedSupplier.businessProfile.orderCutoffTime}
                  onChange={(event) => commitState((current) => patchSupplierBusinessProfile(current, selectedSupplier.id, { orderCutoffTime: event.target.value }))}
                />
              </label>
              <label>
                거래처 상태
                <select
                  value={selectedSupplier.status}
                  onChange={(event) => commitState((current) => patchSupplier(current, selectedSupplier.id, { status: event.target.value as SupplierAutoStatus }))}
                >
                  <option value="active">사용</option>
                  <option value="paused">중지</option>
                </select>
              </label>
              <label>
                주문 채널
                <select
                  value={selectedSupplier.orderChannel}
                  onChange={(event) => commitState((current) => patchSupplier(current, selectedSupplier.id, { orderChannel: event.target.value as SupplierAutoOrderChannel }))}
                >
                  {orderChannelOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                결제 방식
                <select
                  value={selectedSupplier.paymentPolicy.mode}
                  onChange={(event) => commitState((current) => patchSupplierPaymentPolicy(current, selectedSupplier.id, { mode: event.target.value as SupplierAutoPaymentMode }))}
                >
                  {paymentModeOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                커넥터 상태
                <select
                  value={selectedSupplier.paymentPolicy.connectorStatus}
                  onChange={(event) => commitState((current) => patchSupplierPaymentPolicy(current, selectedSupplier.id, { connectorStatus: event.target.value as SupplierAutoConnectorStatus }))}
                >
                  {connectorStatusOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                승인 기준
                <select
                  value={selectedSupplier.paymentPolicy.approvalMode}
                  onChange={(event) => commitState((current) => patchSupplierPaymentPolicy(current, selectedSupplier.id, { approvalMode: event.target.value as SupplierAutoApprovalMode }))}
                >
                  {approvalModeOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                정산 주기
                <select
                  value={selectedSupplier.paymentPolicy.settlementPolicy.settlementCycle}
                  onChange={(event) => commitState((current) => patchSupplierSettlementPolicy(current, selectedSupplier.id, { settlementCycle: event.target.value as SupplierAutoSettlementCycle }))}
                >
                  {settlementCycleOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                은행
                <input
                  value={selectedSupplier.paymentPolicy.settlementPolicy.bankName}
                  onChange={(event) => commitState((current) => patchSupplierSettlementPolicy(current, selectedSupplier.id, { bankName: event.target.value }))}
                />
              </label>
              <label>
                계좌번호
                <input
                  value={selectedSupplier.paymentPolicy.settlementPolicy.bankAccount}
                  onChange={(event) => commitState((current) => patchSupplierSettlementPolicy(current, selectedSupplier.id, { bankAccount: event.target.value }))}
                />
              </label>
              <label>
                예금주
                <input
                  value={selectedSupplier.paymentPolicy.settlementPolicy.accountHolder}
                  onChange={(event) => commitState((current) => patchSupplierSettlementPolicy(current, selectedSupplier.id, { accountHolder: event.target.value }))}
                />
              </label>
              <label>
                세금계산서 이메일
                <input
                  value={selectedSupplier.paymentPolicy.settlementPolicy.taxInvoiceEmail}
                  onChange={(event) => commitState((current) => patchSupplierSettlementPolicy(current, selectedSupplier.id, { taxInvoiceEmail: event.target.value }))}
                />
              </label>
              <label>
                외부 마켓
                <input
                  value={selectedSupplier.externalMarketProfile.marketName}
                  onChange={(event) => commitState((current) => patchSupplierExternalMarketProfile(current, selectedSupplier.id, { marketName: event.target.value }))}
                />
              </label>
              <label>
                마켓 계정
                <input
                  value={selectedSupplier.externalMarketProfile.accountLabel}
                  onChange={(event) => commitState((current) => patchSupplierExternalMarketProfile(current, selectedSupplier.id, { accountLabel: event.target.value }))}
                />
              </label>
              <label>
                주문 페이지
                <input
                  value={selectedSupplier.externalMarketProfile.orderPageUrl}
                  onChange={(event) => commitState((current) => patchSupplierExternalMarketProfile(current, selectedSupplier.id, { orderPageUrl: event.target.value }))}
                />
              </label>
              <label>
                검증일
                <input
                  type="date"
                  value={selectedSupplier.externalMarketProfile.lastVerifiedAt}
                  onChange={(event) => commitState((current) => patchSupplierExternalMarketProfile(current, selectedSupplier.id, { lastVerifiedAt: event.target.value }))}
                />
              </label>
            </div>

            <div className="supplier-auto-wide-fields">
              <label>
                주소
                <textarea
                  value={selectedSupplier.businessProfile.address}
                  onChange={(event) => commitState((current) => patchSupplierBusinessProfile(current, selectedSupplier.id, { address: event.target.value }))}
                />
              </label>
              <label>
                기본 배송 메모
                <textarea
                  value={selectedSupplier.businessProfile.defaultDeliveryMemo}
                  onChange={(event) => commitState((current) => patchSupplierBusinessProfile(current, selectedSupplier.id, { defaultDeliveryMemo: event.target.value }))}
                />
              </label>
              <label>
                결제 메모
                <textarea
                  value={selectedSupplier.paymentPolicy.memo}
                  onChange={(event) => commitState((current) => patchSupplierPaymentPolicy(current, selectedSupplier.id, { memo: event.target.value }))}
                />
              </label>
              <label>
                MCP 메모
                <textarea
                  value={selectedSupplier.externalMarketProfile.automationMemo}
                  onChange={(event) => commitState((current) => patchSupplierExternalMarketProfile(current, selectedSupplier.id, { automationMemo: event.target.value }))}
                />
              </label>
              <label>
                발주 메모
                <textarea
                  value={selectedSupplier.orderMemo}
                  onChange={(event) => commitState((current) => patchSupplier(current, selectedSupplier.id, { orderMemo: event.target.value }))}
                />
              </label>
            </div>

            <SupplierSetupIssues issues={selectedSupplierIssues} />
          </section>

          <section className="supplier-auto-panel" aria-label="거래처별 물품 등록">
            <div className="supplier-auto-panel-title">
              <h2>거래처별 물품 등록</h2>
              <span>{selectedSupplier.supplyItems.length}개 연결</span>
            </div>

            <div className="supplier-auto-mapping-workbench">
              <div className="supplier-auto-search-row">
                <label>
                  재료명 검색
                  <input
                    value={materialSearch}
                    onChange={(event) => setMaterialSearch(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={pendingMaterials.length === 0}
                  onClick={() => {
                    commitState((current) => addSupplyItemMappings(current, selectedSupplier.id, pendingMaterialIds));
                    setPendingMaterialIds([]);
                    setMaterialSearch('');
                  }}
                >
                  선택한 품목 저장 ({pendingMaterials.length})
                </button>
              </div>

              <div className="supplier-auto-material-results" aria-label="재료 검색 결과">
                {materialChoices.map((material) => {
                  const isMapped = mappedMaterialIds.has(material.id);
                  const isPending = pendingMaterialIdSet.has(material.id);
                  return (
                    <article
                      className={`supplier-auto-material-result ${isMapped ? 'is-mapped' : ''} ${isPending ? 'is-pending' : ''}`}
                      key={material.id}
                    >
                      <div>
                        <strong>{material.name}</strong>
                        <small>{material.locationName} · {material.category} · {material.orderUnit}</small>
                      </div>
                      <span>{isMapped ? '연결됨' : isPending ? '담김' : '대기'}</span>
                      <button
                        type="button"
                        disabled={isMapped || isPending}
                        onClick={() => setPendingMaterialIds((current) => (
                          current.includes(material.id) ? current : [...current, material.id]
                        ))}
                      >
                        담기
                      </button>
                    </article>
                  );
                })}
              </div>

              {pendingMaterials.length > 0 && (
                <div className="supplier-auto-pending-tray" aria-label="추가할 제품">
                  {pendingMaterials.map((material) => (
                    <button
                      type="button"
                      key={material.id}
                      onClick={() => setPendingMaterialIds((current) => current.filter((materialId) => materialId !== material.id))}
                    >
                      {material.name}
                      <span>×</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="supplier-auto-table-wrap">
              <table className="supplier-auto-table">
                <thead>
                  <tr>
                    <th>재료</th>
                    <th>거래처 품목명</th>
                    <th>단위</th>
                    <th>팩</th>
                    <th>최소</th>
                    <th>단가</th>
                    <th>리드타임</th>
                    <th>메모</th>
                    <th>자동</th>
                    <th>삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSupplier.supplyItems.map((item) => {
                    const material = state.materials.find((candidate) => candidate.id === item.materialId);
                    return (
                      <tr key={item.id}>
                        <td>
                          <strong>{material?.name ?? '알 수 없음'}</strong>
                          <small>{material?.locationName ?? '-'}</small>
                        </td>
                        <td>
                          <input
                            value={item.supplierSku}
                            onChange={(event) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { supplierSku: event.target.value }))}
                          />
                        </td>
                        <td>
                          <input
                            value={item.orderUnit}
                            onChange={(event) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { orderUnit: event.target.value }))}
                          />
                        </td>
                        <td>
                          <NumberInput
                            value={item.packSize}
                            min={0}
                            step={0.5}
                            onChange={(value) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { packSize: value }))}
                          />
                        </td>
                        <td>
                          <NumberInput
                            value={item.minOrderQuantity}
                            min={0}
                            step={0.5}
                            onChange={(value) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { minOrderQuantity: value }))}
                          />
                        </td>
                        <td>
                          <NumberInput
                            value={item.unitPrice}
                            min={0}
                            step={100}
                            onChange={(value) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { unitPrice: value }))}
                          />
                        </td>
                        <td>
                          <NumberInput
                            value={item.leadTimeDays}
                            min={0}
                            step={1}
                            onChange={(value) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { leadTimeDays: value }))}
                          />
                        </td>
                        <td>
                          <input
                            value={item.memo}
                            onChange={(event) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { memo: event.target.value }))}
                          />
                        </td>
                        <td>
                          <label className="supplier-auto-switch">
                            <input
                              type="checkbox"
                              checked={item.autoOrderEnabled}
                              onChange={(event) => commitState((current) => patchSupplyItem(current, selectedSupplier.id, item.id, { autoOrderEnabled: event.target.checked }))}
                            />
                            <span />
                          </label>
                        </td>
                        <td>
                          <button
                            className="supplier-auto-icon-button"
                            type="button"
                            title="품목 삭제"
                            onClick={() => commitState((current) => removeSupplyItemMapping(current, selectedSupplier.id, item.id))}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="supplier-auto-panel supplier-auto-drafts" aria-label="자동발주 후보">
            <div className="supplier-auto-panel-title">
              <h2>자동발주 후보</h2>
              <span>{formatSupplierAutoMoney(supplierDraftTotal)}</span>
            </div>
            {selectedSupplierDrafts.length === 0 ? (
              <div className="supplier-auto-empty">현재 후보 없음</div>
            ) : (
              <div className="supplier-auto-draft-list">
                {selectedSupplierDrafts.map((draft) => (
                  <DraftCard
                    key={draft.id}
                    draft={draft}
                    isActive={draft.id === activeDraft?.id}
                    onActivate={() => commitState((current) => ({ ...current, activeDraftId: draft.id }))}
                    onQuantityChange={(lineId, quantity) => commitState((current) => setDraftLineQuantity(current, draft.id, lineId, quantity))}
                    onExcludedChange={(lineId, excluded) => commitState((current) => setDraftLineExcluded(current, draft.id, lineId, excluded))}
                    onApprove={() => commitState((current) => approveDraft(current, draft.id))}
                    onHold={() => commitState((current) => holdDraft(current, draft.id))}
                  />
                ))}
              </div>
            )}
          </section>
        </main>

        <aside className="supplier-auto-panel supplier-auto-side" aria-label="승인 미리보기">
          <div className="supplier-auto-panel-title">
            <h2>승인 미리보기</h2>
            <span>{activeDraft?.status ?? 'none'}</span>
          </div>
          {activeDraft ? (
            <>
              <div className="supplier-auto-preview-meta">
                <span>{activeDraft.supplierName}</span>
                <strong>{formatSupplierAutoMoney(activeDraft.totalAmount)}</strong>
              </div>
              <textarea className="supplier-auto-phone-preview" value={activeDraft.phonePreview} readOnly />
              <div className="supplier-auto-checks">
                <CheckRow label="결제" value={paymentModeLabels[activeDraft.paymentPolicy.mode]} />
                <CheckRow label="채널" value={orderChannelLabels[activeDraft.orderChannel]} />
                <CheckRow label="정산" value={settlementCycleLabels[activeDraft.paymentPolicy.settlementPolicy.settlementCycle]} />
                <CheckRow label="커넥터" value={connectorStatusLabels[activeDraft.paymentPolicy.connectorStatus]} />
                <CheckRow label="외부마켓" value={activeDraftSupplier?.externalMarketProfile.marketName || '-'} />
              </div>
              {activeDraft.warnings.length > 0 && (
                <div className="supplier-auto-warning-list">
                  {activeDraft.warnings.map((warning) => (
                    <p key={warning}>{warning}</p>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="supplier-auto-empty">선택된 후보 없음</div>
          )}

          <CoupangMcpHarness scenario={coupangMcpScenario} />
          <SosAdapterHarness payload={adapterPayload} selectedUpsert={selectedSupplierSosUpsert} />

          <div className="supplier-auto-timeline">
            <h3>히스토리</h3>
            {state.history.map((entry) => (
              <div className="supplier-auto-timeline-item" key={entry.id}>
                <time>{formatTime(entry.at)}</time>
                <strong>{entry.action}</strong>
                <p>{entry.detail}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

function CoupangMcpHarness({ scenario }: { scenario: CoupangMcpScenario }) {
  const payloadText = JSON.stringify(scenario.payload, null, 2);
  const statusClass = `supplier-auto-mcp-harness is-${scenario.status.replace('_', '-')}`;

  return (
    <div className={statusClass} aria-label="쿠팡 MCP 시나리오">
      <div className="supplier-auto-adapter-head supplier-auto-mcp-head">
        <strong>쿠팡 MCP 어댑터</strong>
        <span>{coupangScenarioStatusLabels[scenario.status]}</span>
      </div>
      <div className="supplier-auto-checks">
        <CheckRow label="거래처" value={scenario.supplierName} />
        <CheckRow label="플랫폼" value={scenario.platformName} />
        <CheckRow label="품목" value={`${scenario.activeLineCount}개`} />
        <CheckRow label="차단" value={`${scenario.blockers.length}개`} />
        <CheckRow label="검토" value={`${scenario.reviewItems.length}개`} />
      </div>
      <div className="supplier-auto-mcp-stage-list">
        {scenario.stages.map((stage) => (
          <div className={`supplier-auto-mcp-stage is-${stage.status.replace('_', '-')}`} key={stage.id}>
            <div>
              <strong>{stage.label}</strong>
              <span>{stage.owner} · {stage.source}</span>
            </div>
            <em>{coupangStageStatusLabels[stage.status]}</em>
            <p>{stage.detail}</p>
          </div>
        ))}
      </div>
      <textarea className="supplier-auto-adapter-json supplier-auto-mcp-json" value={payloadText} readOnly />
    </div>
  );
}

function SosAdapterHarness({
  payload,
  selectedUpsert,
}: {
  payload: SupplierAutoSosAdapterPayload;
  selectedUpsert: SupplierAutoSosSupplierUpsert | null;
}) {
  const selectedPayloadText = selectedUpsert
    ? JSON.stringify(selectedUpsert.input, null, 2)
    : '{}';

  return (
    <div className="supplier-auto-adapter-harness" aria-label="SOS 어댑터 하네스">
      <div className="supplier-auto-adapter-head">
        <strong>SOS 어댑터 하네스</strong>
        <span>{payload.report.ready ? 'adapterReady' : 'adapterBlocked'}</span>
      </div>
      <div className="supplier-auto-checks">
        <CheckRow label="API" value={selectedUpsert?.endpoint ?? '-'} />
        <CheckRow label="upsert" value={`${payload.report.upsertCount}건 (${payload.report.createCount} 생성 / ${payload.report.updateCount} 수정)`} />
        <CheckRow label="table" value={payload.report.sourceTable} />
        <CheckRow label="metadata" value={`metadata.${payload.report.metadataKey}`} />
        <CheckRow label="검증" value={`차단 ${payload.report.blockingIssueCount} · 확인 ${payload.report.warningIssueCount}`} />
      </div>
      <textarea className="supplier-auto-adapter-json" value={selectedPayloadText} readOnly />
    </div>
  );
}

function Metric({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'warning' }) {
  return (
    <div className={`supplier-auto-metric ${tone === 'warning' ? 'is-warning' : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SupplierSetupIssues({ issues }: { issues: SupplierAutoValidationIssue[] }) {
  if (issues.length === 0) {
    return (
      <div className="supplier-auto-setup-status is-ready">
        <strong>거래처 설정 완료</strong>
        <span>자동발주가 읽을 기본 정보와 공급물품 기준이 준비되었습니다.</span>
      </div>
    );
  }

  return (
    <div className="supplier-auto-setup-status">
      <div className="supplier-auto-setup-status-head">
        <strong>거래처 설정 확인</strong>
        <span>{issues.filter((issue) => issue.severity === 'error').length}개 차단 · {issues.filter((issue) => issue.severity === 'warning').length}개 확인</span>
      </div>
      <div className="supplier-auto-issue-list">
        {issues.map((issue) => (
          <p className={`supplier-auto-issue is-${issue.severity}`} key={issue.id}>
            <strong>{issue.title}</strong>
            <span>{issue.detail}</span>
          </p>
        ))}
      </div>
    </div>
  );
}

function NumberInput({
  value,
  min,
  step,
  onChange,
}: {
  value: number;
  min?: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <input
      type="number"
      value={Number.isFinite(value) ? value : 0}
      min={min}
      step={step}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  );
}

function DraftCard({
  draft,
  isActive,
  onActivate,
  onQuantityChange,
  onExcludedChange,
  onApprove,
  onHold,
}: {
  draft: SupplierAutoDraft;
  isActive: boolean;
  onActivate: () => void;
  onQuantityChange: (lineId: string, quantity: number) => void;
  onExcludedChange: (lineId: string, excluded: boolean) => void;
  onApprove: () => void;
  onHold: () => void;
}) {
  return (
    <article className={`supplier-auto-draft ${isActive ? 'is-active' : ''}`} onFocus={onActivate}>
      <div className="supplier-auto-draft-head">
        <button type="button" onClick={onActivate}>
          <strong>{draft.supplierName}</strong>
          <span>{draft.lines.length}개 품목 · {formatSupplierAutoMoney(draft.totalAmount)}</span>
        </button>
        <em>{draft.status}</em>
      </div>

      <div className="supplier-auto-draft-lines">
        {draft.lines.map((line) => (
          <div className={`supplier-auto-draft-line ${line.excluded ? 'is-excluded' : ''}`} key={line.id}>
            <div>
              <strong>{line.materialName}</strong>
              <small>{line.locationName} · 현재 {line.currentStock} / 최소 {line.minStock} / 목표 {line.targetStock}</small>
              <small>{line.reason}</small>
            </div>
            <label>
              수량
              <NumberInput
                value={line.orderQuantity}
                min={0}
                step={0.5}
                onChange={(value) => onQuantityChange(line.id, value)}
              />
            </label>
            <span>{line.orderUnit}</span>
            <strong>{formatSupplierAutoMoney(line.amount)}</strong>
            <label className="supplier-auto-check">
              <input
                type="checkbox"
                checked={!line.excluded}
                onChange={(event) => onExcludedChange(line.id, !event.target.checked)}
              />
              포함
            </label>
          </div>
        ))}
      </div>

      <div className="supplier-auto-actions">
        <button type="button" onClick={onApprove}>승인 시뮬레이션</button>
        <button type="button" onClick={onHold}>보류</button>
      </div>
    </article>
  );
}

function CheckRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="supplier-auto-check-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
